import React, { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { infrastructureContent } from './Infrastructure.content';
import logo from '../Feature/assets/logo.svg';
import cloud1 from '../Peculiarities/assets/cloud-1.webp';
import cloud2 from '../Peculiarities/assets/cloud-2.webp';
import cloud3 from '../Peculiarities/assets/cloud-3.webp';
import cloud4 from '../Peculiarities/assets/cloud-4.webp';
import styles from './Infrastructure.module.sass';

gsap.registerPlugin(ScrollTrigger);

export function Infrastructure() {
  const { title, subtitle, cards, area } = infrastructureContent;
  const sectionRef = useRef(null);
  const cardsRef = useRef(null);
  const areaHeaderRef = useRef(null);
  const areaStatsRef = useRef(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const cardsContainer = cardsRef.current;
    const areaHeader = areaHeaderRef.current;
    const areaStats = areaStatsRef.current;

    if (!section || !cardsContainer) return;

    let areaRevealDone = false;

    const tryRevealArea = (self) => {
      if (areaRevealDone || !areaHeader || !areaStats) return;
      if (self.progress > 0.99) {
        areaRevealDone = true;
        gsap.to([areaHeader, areaStats], {
          opacity: 1,
          filter: 'blur(0px)',
          duration: 0.85,
          ease: 'power2.out',
          stagger: 0.12,
        });
      }
    };

    const ctx = gsap.context(() => {
      const startOffset = window.innerHeight <= 800 ? 'top top-=350px' : window.innerHeight <= 900 ? 'top top-=280px' : 'top top-=200px';

      ScrollTrigger.create({
        trigger: section,
        pin: true,
        start: startOffset,
        end: () => `+=${cardsContainer.scrollWidth - window.innerWidth}`,
        scrub: 1,
        invalidateOnRefresh: true,
        anticipatePin: 1,
        refreshPriority: -30,
        animation: gsap.to(cardsContainer, {
          x: () => -(cardsContainer.scrollWidth - window.innerWidth),
          ease: 'none',
        }),
        onUpdate(self) {
          tryRevealArea(self);
        },
        onRefresh(self) {
          tryRevealArea(self);
        },
      });
    }, section);

    const refresh = () => {
      ScrollTrigger.sort();
      ScrollTrigger.refresh();
    };

    const rafId = requestAnimationFrame(refresh);
    window.addEventListener('load', refresh);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('load', refresh);
      ctx.revert();
    };
  }, []);

  return (
    <section className={styles.infra} ref={sectionRef}>
      <div className={styles.infra__bg} aria-hidden />
      <div className={styles.infra__bg2} aria-hidden />
      <div className={styles.infra__clouds} aria-hidden>
        <img src={cloud1} alt="" className={styles['infra__cloud--1']} />
        <img src={cloud2} alt="" className={styles['infra__cloud--2']} />
        <img src={cloud3} alt="" className={styles['infra__cloud--3']} />
        <img src={cloud4} alt="" className={styles['infra__cloud--4']} />
      </div>

      <div className={styles.infra__inner}>
        <header className={styles.infra__header}>
          <div className={styles.infra__logo}>
            <img src={logo} alt="" />
          </div>
          <h2 className={styles.infra__title}>
            {title.map((seg, i) => (
              <span key={i} className={styles.infra__titleLine}>
                <span className={seg.accent ? styles['infra__titleLine--accent'] : undefined}>
                  {seg.text}
                </span>
              </span>
            ))}
          </h2>
          <p className={styles.infra__subtitle}>
            {subtitle.map((seg, i) => (
              <span
                key={i}
                className={`${styles.infra__subtitleSeg} ${seg.accent ? styles['infra__subtitleSeg--accent'] : ''}`}
              >
                {seg.text.split('\n').map((line, index) => (
                  <span key={index}>
                    {line}
                    {index < seg.text.split('\n').length - 1 && <br />}
                  </span>
                ))}
              </span>
            ))}
          </p>
        </header>

        <div className={styles.infra__cards} ref={cardsRef}>
          {cards.map((card, i) => (
            <article key={i} className={`${styles.infra__card} ${styles[`infra__card--${i + 1}`]}`}>
              <div className={styles.infra__cardImageWrap}>
                <img src={card.image} alt="" className={styles.infra__cardImage} loading="lazy" />
              </div>
              <div className={styles.infra__cardBody}>
                <p className={styles.infra__cardTitle}>
                  {card.title.map((line, j) => (
                    <span key={j} className={styles.infra__cardTitleLine}>
                      {line.map((seg, k) => (
                        <span
                          key={k}
                          className={
                            seg.accent ? styles['infra__cardTitleLine--accent'] : undefined
                          }
                        >
                          {seg.text}
                        </span>
                      ))}
                    </span>
                  ))}
                </p>
                <p className={styles.infra__cardText}>
                  {card.text.map((seg, j) => (
                    <span key={j}>
                      <span className={seg.accent ? styles['infra__cardText--accent'] : undefined}>
                        {seg.text}
                      </span>
                      {j < card.text.length - 1 ? ' ' : ''}
                    </span>
                  ))}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>

      <div className={styles.infra__area}>
        <header className={styles.infra__areaHeader} ref={areaHeaderRef}>
          <p className={styles.infra__areaKicker}>{area.kicker}</p>
          <div className={styles.infra__areaTitle}>
            {area.title.map((line, i) => (
              <div key={i} className={styles.infra__areaTitleLine}>
                {line.map((seg, j) => (
                  <span key={j} className={seg.accent ? styles['infra__areaTitleLine--accent'] : undefined}>
                    {seg.text}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </header>
        <div className={styles.infra__areaStats} ref={areaStatsRef}>
          {area.stats.map((stat, i) => (
            <div key={i}>
              <p className={styles.infra__statValue}>{stat.value}</p>
              <p className={styles.infra__statLabel}>
                {stat.label.map((seg, j) => (
                  <span
                    key={j}
                    className={`${styles.infra__statLabelSeg} ${seg.accent ? styles['infra__statLabelSeg--accent'] : ''}`}
                  >
                    {seg.text}
                  </span>
                ))}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
